import { API_BASE } from "@/lib/constants";
import type { LookupResponse } from "@/lib/types";

// ── Data selection catalogs ───────────────────────────────────────────────
// A selectable unit of a lookup's response. `free` items come from keyless /
// free providers; paid items require a configured API key. These mirror the
// backend catalogs (handlers/ip_catalog.go, handlers/google_catalog.go).
export interface DataCatalogItem {
  id: string;
  label: string;
  free: boolean;
}

// Dedicated email sub-endpoints. `emailValidity` is a lean deliverability-only
// check; `emailIdentity` scopes the enrichment server-side and
// returns the same lookup payload the focused pages render.

/** One of the four public deliverability statuses. */
export type ValidityStatus = "valid" | "invalid" | "catch-all" | "risky";

export interface ValidityDomainTrust {
  grade: string; // A–F
  spf: boolean;
  dmarc: boolean;
  dmarc_policy?: string; // none | quarantine | reject
  mta_sts?: string; // none | testing | enforce
  tls_rpt: boolean;
  // Raw DNS records behind the flags.
  spf_record?: string;
  dmarc_record?: string;
  tls_rpt_record?: string;
  mta_sts_record?: string;
  // Authentication extras.
  dkim?: boolean;
  dkim_selectors?: string[];
  bimi?: boolean;
  bimi_record?: string;
  dnssec?: boolean;
}

// B: domain registration + DNS posture.
export interface ValidityDomainInfo {
  registrar?: string;
  created_at?: string;
  expires_at?: string;
  age_days?: number;
  nameservers?: string[];
  dnssec?: boolean;
  a?: string[];
}

// B: a resolved MX host with hosting + reputation intel.
export interface ValidityMailServer {
  host: string;
  ip?: string;
  asn?: string;
  org?: string;
  country?: string;
  hosting?: boolean;
  blocklisted?: boolean;
  blocklists?: string[];
  // Explicit STARTTLS posture from the active :25 probe. Always set for probed
  // hosts so the UI never renders a blank TLS column.
  tls_status?: "supported" | "not_supported" | "unknown";
  tls_version?: string;
  tls_reason?: string;
  tls_issuer?: string;
  tls_expires_at?: string;
  tls_days_left?: number;
}

// D: identity footprint.
export interface ValidityFootprintService {
  name: string;
  category?: string;
  url?: string;
}
export interface ValidityGoogleFootprint {
  name?: string;
  profile_photo_url?: string;
  gaia_id?: string;
  active_services?: string[];
  last_profile_edit?: string;
}
export interface ValidityBreachEntry {
  name: string;
  date?: string;
  data_types?: string[];
}
export interface ValidityBreachSummary {
  count: number;
  exposed_data?: string[];
  services?: ValidityBreachEntry[];
}
export interface ValidityGravatarVerifiedAccount {
  service_type?: string;
  service_label?: string;
  url?: string;
}
export interface ValidityGravatarProfile {
  display_name?: string;
  avatar_url?: string;
  profile_url?: string;
  location?: string;
  job_title?: string;
  company?: string;
  description?: string;
  pronouns?: string;
  verified_accounts?: ValidityGravatarVerifiedAccount[];
}
export interface ValidityFootprint {
  gravatar_url?: string;
  gravatar_profile?: ValidityGravatarProfile;
  registered_services?: ValidityFootprintService[];
  recovery_emails?: string[];
  recovery_phones?: string[];
  google?: ValidityGoogleFootprint;
  breaches?: ValidityBreachSummary;
}

export interface ValidityPersonSignal {
  count: number;
  sources: string[]; // e.g. ["hibp", "gravatar"]
}

export interface ValiditySMTP {
  mx_host?: string;
  code?: number;
  message?: string;
  catch_all: boolean;
  greylisted: boolean;
}

export interface EmailValidityResult {
  email: string;
  status: ValidityStatus;
  reason?: string;
  /** Whether the domain is a disposable / throwaway email provider. */
  disposable?: boolean;
  confidence?: string; // high | medium | low (provider-calibrated)
  role: boolean;
  role_name?: string;
  did_you_mean?: string;
  domain: string;
  provider?: string; // google | microsoft | yahoo | other
  mx?: string[]; // resolved mail servers, in preference order
  domain_trust?: ValidityDomainTrust;
  person_signal?: ValidityPersonSignal;
  smtp?: ValiditySMTP;
  checked_at: string;
  // End-to-end enrichment (plan A–D).
  canonical?: string;
  free_provider?: boolean;
  domain_info?: ValidityDomainInfo;
  mail_servers?: ValidityMailServer[];
  footprint?: ValidityFootprint;
  /** Human-readable summary of the status, authored by the backend. */
  message?: string;
  /** @deprecated legacy string mirror of `status`. */
  validity?: string;
  credits: number;
  /** Set when the result was served from the server-side cache (no re-compute). */
  cached?: boolean;
}

// Dedicated email breach exposure (HIBP) check. Returns the rich breach_info
// payload used by the Breaches page. Charges like a standard lookup.
export interface EmailBreachService {
  name: string;
  domain?: string;
  breach_date?: string;
  data_types?: string[];
}

export interface EmailBreachInfo {
  breach_count: number;
  services: EmailBreachService[];
  interests?: { category: string; signal: number }[];
  exposed_data?: string[];
}

export interface EmailBreachesResult {
  email: string;
  breached: boolean;
  breach_info: EmailBreachInfo;
  credits: number;
}

// Public landing-page demo lookup - unauthenticated, rate-limited, no credits.
export async function demoLookupEmail(email: string) {
  const res = await fetch(`${API_BASE}/api/tools/demo/lookup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Request failed" }));
    const e = new Error(err.error || `Request failed with status ${res.status}`) as Error & { status?: number };
    e.status = res.status;
    throw e;
  }

  return res.json();
}

// Public landing-page demo email sub-lookups (validity / identity / breaches /
// password) - unauthenticated, rate-limited, no credits. Mirror demoLookupEmail.
async function postDemo(path: string, body: Record<string, unknown>) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Request failed" }));
    const e = new Error(err.error || `Request failed with status ${res.status}`) as Error & { status?: number };
    e.status = res.status;
    throw e;
  }
  return res.json();
}

export function demoEmailValidity(email: string): Promise<EmailValidityResult> {
  return postDemo("/api/tools/demo/email-validity", { email });
}

export function demoEmailIdentity(email: string): Promise<LookupResponse> {
  return postDemo("/api/tools/demo/email-identity", { email });
}

export function demoEmailBreaches(email: string): Promise<EmailBreachesResult> {
  return postDemo("/api/tools/demo/email-breaches", { email });
}

export async function demoPasswordBreaches(password: string): Promise<PasswordBreachResult> {
  const sha1 = await sha1Hex(password);
  return postDemo("/api/tools/demo/password-breaches", { sha1 });
}

export interface EmailGenderResult {
  email: string;
  name?: string;
  gender: "male" | "female" | "unknown";
  country: string | null;
  probability: number;
  credits: number;
}

export function demoEmailGender(email: string): Promise<EmailGenderResult> {
  return postDemo("/api/tools/demo/email-gender", { email });
}

// ── Email compliance (demo) ────────────────────────────────────────────────

export interface EmailComplianceSignals {
  points: number;
  max_points: number;
  checks: number;
  latency_ms: number;
}

export interface EmailComplianceResult {
  email: string;
  /** Empty string when no jurisdiction could be established. */
  country: string;
  country_code: string;
  email_type: string;
  recommendation_code: string;
  recommendation: string;
  main_laws: string[];
  law_reference_urls: string[];
  main_restrictions: string[];
  applicability: string;
  confidence: string;
  /** omitempty on the backend - absent rather than empty. */
  jurisdictions_applicable?: string[];
  /** omitempty on the backend - absent rather than empty. */
  possible_travel?: string[];
  signals?: EmailComplianceSignals;
  credits: number;
  /** Lifted from the response envelope - a user-facing sentence. */
  message: string;
}

// Unlike the other demo endpoints this one returns the {success, result,
// message} envelope, so it can't reuse postDemo. Unwrap it and lift `message`
// onto the result so callers see the same bare shape as the other checks.
// Errors carry a user-facing `message` and a machine-readable `result.code`.
export async function demoEmailCompliance(email: string): Promise<EmailComplianceResult> {
  const res = await fetch(`${API_BASE}/api/tools/demo/email-compliance`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  const body = (await res.json().catch(() => null)) as
    | { success?: boolean; result?: unknown; message?: string }
    | null;

  if (!res.ok || !body?.success) {
    const e = new Error(body?.message || `Request failed with status ${res.status}`) as Error & {
      status?: number;
      code?: string;
    };
    e.status = res.status;
    e.code = (body?.result as { code?: string } | undefined)?.code;
    throw e;
  }

  return { ...(body.result as EmailComplianceResult), message: body.message ?? "" };
}
// ── Password breaches (demo) ────────────────────────────────────────────────

export interface PasswordBreachResult {
  prefix: string;
  found: boolean;
  count: number;
  message: string;
  credits: number;
}

// sha1Hex returns the upper-case hex SHA-1 of a string, computed in the browser.
async function sha1Hex(input: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
}

// ── GDPR: delete my data ───────────────────────────────────────────────────

export interface GdprEmailRecord {
  type?: string;
  query?: string;
  created_at?: string;
  data?: unknown;
}
export interface GdprDataReport {
  cached_lookups: GdprEmailRecord[];
  lookup_history: GdprEmailRecord[];
  permacache: GdprEmailRecord[];
}
export interface GdprAccount {
  email: string;
  name?: string;
  plan?: string;
  created_at?: string;
}
export interface GdprVerifyResult {
  verified: boolean;
  token: string;
  account?: GdprAccount;
  data?: GdprDataReport;
}

async function gdprPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 429) {
      throw new Error("Too many attempts. Please wait a minute and try again.");
    }
    const err = await res.json().catch(() => ({ error: "Request failed" }));
    throw new Error(err.error || `Request failed with status ${res.status}`);
  }
  return res.json();
}

export function gdprRequestCode(email: string): Promise<{ sent: boolean }> {
  return gdprPost("/api/account/gdpr/request-code", { email });
}

export function gdprVerify(email: string, code: string): Promise<GdprVerifyResult> {
  return gdprPost("/api/account/gdpr/verify", { email, code });
}

export function gdprDelete(token: string): Promise<{ deleted: boolean }> {
  return gdprPost("/api/account/gdpr/delete", { token });
}

// ── AI summary ────────────────────────────────────────────────────────────

// summarizeLookup sends a lookup result to the backend's cheap AI summarizer
// and returns a short plain-language overview. `signal` lets the caller abort.
export async function summarizeLookup(
  kind: string,
  data: unknown,
  opts?: { query?: string; signal?: AbortSignal },
): Promise<string> {
  const res = await fetch(`${API_BASE}/api/summarize`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ kind, query: opts?.query, data }),
    signal: opts?.signal,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Request failed" }));
    throw new Error(err.error || `Request failed with status ${res.status}`);
  }
  const json = await res.json();
  return json.summary as string;
}

// ── Feedback ──────────────────────────────────────────────

export async function submitFeedback(data: { endpoint: string; rating: "positive" | "negative"; comment?: string; request_id?: string }) {
  const res = await fetch(`${API_BASE}/api/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to submit feedback");
  return res.json();
}

